import type { OptionsFilter } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { Org } from '@motionsafe/ms-console-api/admin/v1/org_pb.js';
import { OrgStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { orgSearchQuery, orgsByIdQuery } from '../api/hooks';

const keepAll: OptionsFilter = ({ options }) => options;

/**
 * The organizations a picker offers, for staff: the first few its search
 * finds, read from the service as the person types, with the one it holds
 * kept among them so its name shows. Spread `select` onto a searchable
 * Select; the service has done the matching, so the Select keeps every
 * option it is given. Nothing is searched until the list is opened.
 */
export function useOrgSearch({ value, activeOnly = false, exclude = '' }: { value: string | null; activeOnly?: boolean; exclude?: string }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced] = useDebouncedValue(search, 200);
  const held = useQuery(orgsByIdQuery(value ? [value] : [])).data?.[0];
  // The name of the one it holds is what the field shows, not a search.
  const term = debounced.trim() === (held?.name ?? '') ? '' : debounced.trim();
  const found = useQuery({ ...orgSearchQuery(term, activeOnly ? OrgStatus.ACTIVE : OrgStatus.UNSPECIFIED), placeholderData: keepPreviousData, enabled: open });
  const orgs: Org[] = [...(held && !found.data?.some((o) => o.id === held.id) ? [held] : []), ...(found.data ?? [])].filter((o) => o.id !== exclude);
  return {
    orgs,
    held,
    select: { searchable: true, searchValue: search, onSearchChange: setSearch, filter: keepAll, onDropdownOpen: () => setOpen(true), onDropdownClose: () => setOpen(false) },
  };
}
